// Shared optimistic "delete one match" mutation, used by both the Matches
// Evaluated tab and CVPicker. Previously copy-pasted in both routes. Deleting
// optimistically removes the row from every cached ['matches', …] query
// instead of invalidating (which refetched the full payload — the original
// "delete is slow" cause). Handles BOTH cache shapes:
//   - infinite query: { pages: [{ items, total }, …], pageParams }
//   - flat query:      { items, total, … }

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDelete, ApiClientError } from './api';
import { queryKeys } from './queryKeys';

function removeIdFromCache<T>(old: T, id: string): T {
  if (!old || typeof old !== 'object') return old;
  const o = old as Record<string, unknown>;
  // Infinite query cache.
  if (Array.isArray(o.pages)) {
    return {
      ...o,
      pages: (o.pages as Array<Record<string, unknown>>).map((p) => {
        const items = Array.isArray(p.items) ? p.items : [];
        const had = items.some((it: { id?: string }) => it.id === id);
        return {
          ...p,
          items: items.filter((it: { id?: string }) => it.id !== id),
          total: typeof p.total === 'number' ? Math.max(0, p.total - (had ? 1 : 0)) : p.total,
        };
      }),
    } as T;
  }
  // Flat query cache.
  if (Array.isArray(o.items)) {
    const had = (o.items as Array<{ id?: string }>).some((it) => it.id === id);
    return {
      ...o,
      items: (o.items as Array<{ id?: string }>).filter((it) => it.id !== id),
      total: typeof o.total === 'number' ? Math.max(0, o.total - (had ? 1 : 0)) : o.total,
    } as T;
  }
  return old;
}

export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<{ ok: true; deleted: string[] }>(`/api/matches/${encodeURIComponent(id)}`),
    onSuccess: (_data, id) => {
      qc.setQueriesData({ queryKey: queryKeys.matchesAll }, (old) => removeIdFromCache(old, id));
      qc.invalidateQueries({ queryKey: queryKeys.matchesPending });
      qc.invalidateQueries({ queryKey: queryKeys.matchesPendingCount });
      // Tailored PDFs may be cleaned up server-side → refresh CVPicker's tab.
      qc.invalidateQueries({ queryKey: queryKeys.cvTailored });
    },
    onError: (e) => {
      if (e instanceof ApiClientError) {
        alert(`Delete failed (${e.status}): ${e.message.slice(0, 200)}`);
      } else {
        alert('Delete failed: ' + (e as Error).message);
      }
    },
  });
}
